import { Box, Button, ButtonDropdown, Header, Link, Pagination, SpaceBetween, StatusIndicator, Table, TextFilter, CollectionPreferences } from "@cloudscape-design/components";
import { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import DemoStatusIndicator from "../DemoStatusIndicator";
import { AdminDemoDefiniton, DemoDefinition, DemoStatus } from "../../types";
import { useAuth } from "react-oidc-context";
import { getEndpoint } from "../../utils";

export default function AdminDemosTable() {
    const navigate = useNavigate();
    const auth = useAuth();
    const [demos, setDemos] = useState<AdminDemoDefiniton[]>([]);
    const [selectedDemos, setSelectedDemos] = useState<AdminDemoDefiniton[]>([]);
    const [demosLoaded, setDemosLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [pageIndex, setPageIndex] = useState(1);
    const [multiSelectActionsDisabled, setMultiSelectActionsDisabled] = useState(true);
    const [singleItemActionsDisabled, setSingleItemActionsDisabled] = useState(true);
    const [filteringText, setFilteringText] = useState("");
    const [preferences, setPreferences] = useState({ pageSize: 10 });

    const handleVisibilityChange = async (visible: boolean) => {
        if (!auth.user?.id_token) return;

        const updatedDemos = await Promise.all(selectedDemos.map(async (demo) => {
            const updatedDemo = { ...demo, visible };
            const response = await fetch(getEndpoint(`/api/admin/demos/${demo.path}`), {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${auth.user!.id_token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(updatedDemo)
            });

            if (!response.ok) {
                console.error(`Failed to update demo ${demo.path}`);
                return demo;
            }
            return updatedDemo;
        }));

        setDemos(demos.map(demo =>
            updatedDemos.find(updated => updated.path === demo.path) || demo
        ));
        setSelectedDemos([]);
    };

    const filteredDemos = demos.filter((demo) => {
        return (
            demo.name?.toLowerCase().includes(filteringText.toLowerCase()) ||
            demo.path?.toLowerCase().includes(filteringText.toLowerCase()) ||
            demo.demoDetails?.sfdcTagName?.toLowerCase().includes(filteringText.toLowerCase()) ||
            demo.demoDetails?.sfdcTagCategory?.toLowerCase().includes(filteringText.toLowerCase())
        );
    });

    const startIndex = (pageIndex - 1) * preferences.pageSize;
    const endIndex = startIndex + preferences.pageSize;
    const currentPageItems = filteredDemos.slice(startIndex, endIndex);

    useEffect(() => {
        if (!demosLoaded) {
            console.log('Loading demos');
            setLoading(true);
            if (!auth.user?.id_token) {
                throw new Error('No authentication token available');
            }
            auth.startSilentRenew();
            const token = auth.user.id_token;
            const endpoint = getEndpoint(`/api/admin/demos`)
            if (!token) {
                return;
            }
            fetch(endpoint, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            }).then((response) => {
                if (!response.ok) {
                    response.text().then((t) => console.error(t));
                } else {
                    response.json().then((data) => {
                        setDemos(data);
                        setDemosLoaded(true);
                        setLoading(false);
                    })
                }
            }).catch((e) => {
                console.error(e);
                setLoading(false);
            });

        }

    }, [demos, demosLoaded, auth])

    return (
        <Table
            renderAriaLive={({
                firstIndex,
                lastIndex,
                totalItemsCount
            }) =>
                `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
            }
            onSelectionChange={({ detail }) => {
                console.log(detail.selectedItems)
                console.log(detail.selectedItems.length === 1)
                setSelectedDemos(detail.selectedItems)
                setMultiSelectActionsDisabled(detail.selectedItems.length < 1)
                setSingleItemActionsDisabled(detail.selectedItems.length !== 1)
            }}
            selectedItems={selectedDemos}
            ariaLabels={{
                selectionGroupLabel: "Item selection",
                allItemsSelectionLabel: () => "Select all items",
            }}
            columnDefinitions={[
                {
                    id: "name",
                    header: "Demo Name",
                    cell: (item) => { return <Link href={`/admin/demo/${encodeURIComponent(item['path'])}`}>{item['name']}</Link> },
                    isRowHeader: true,
                },
                {
                    id: "path",
                    header: "Unique Demo Key",
                    cell: (item) => item['path'],
                },
                {
                    id: "state",
                    header: "Demo Status",
                    cell: (item: DemoDefinition) => { return item.status ? <DemoStatusIndicator status={item.status} /> : <DemoStatusIndicator status={DemoStatus.production} /> },
                },
                {
                    id: "sfdcTagCategory",
                    header: "SFDC Tag Category",
                    cell: (item: AdminDemoDefiniton) => item.demoDetails?.sfdcTagCategory || "-",
                },
                {
                    id: "sfdcTagName",
                    header: "SFDC Tag Name",
                    cell: (item: AdminDemoDefiniton) => item.demoDetails?.sfdcTagName || "-",
                },
                {
                    id: "visibility",
                    header: "Visibility",
                    cell: (item: AdminDemoDefiniton) => (item.visible !== false || !item.deployedToPortal)  ?
                        <StatusIndicator type="success">Visible</StatusIndicator> :
                        <StatusIndicator type="error">Hidden</StatusIndicator>,
                },
                {
                    id: "deployed",
                    header: "Demo Deployed",
                    cell: (item: AdminDemoDefiniton) => item.deployedToPortal ? <StatusIndicator type="success">Yes</StatusIndicator> : <StatusIndicator type="warning">No</StatusIndicator>,
                }
            ]}
            columnDisplay={[
                { id: "name", visible: true },
                { id: "path", visible: true },
                { id: "state", visible: true },
                { id: "sfdcTagCategory", visible: true },
                { id: "sfdcTagName", visible: true },
                { id: "visibility", visible: true },
                { id: "deployed", visible: true }
            ]}
            items={currentPageItems}
            loadingText="Loading items..."
            loading={loading}
            trackBy="path"
            selectionType="multi"
            empty={
                <Box
                    margin={{ top: "l" }}
                    textAlign="center"
                    color="inherit"
                >
                    <SpaceBetween size="m">
                        <b>It's quiet... too quiet. No demos were loaded...</b>
                    </SpaceBetween>
                </Box>
            }
            filter={
                <TextFilter
                    filteringPlaceholder="Find resources"
                    filteringText={filteringText}
                    onChange={({ detail }) => {
                        setFilteringText(detail.filteringText);
                        setPageIndex(1);
                    }}
                />
            }
            header={
                <Header
                    counter={
                        selectedDemos.length ? "(" + selectedDemos.length + "/" + demos.length + ")" : "(" + demos.length + ")"
                    }
                    actions={
                        <SpaceBetween direction="horizontal" size="xs">
                            <ButtonDropdown
                                items={[
                                    {
                                        text: "Show",
                                        id: "show",
                                        disabled: multiSelectActionsDisabled
                                    },
                                    {
                                        text: "Hide",
                                        id: "hide",
                                        disabled: multiSelectActionsDisabled
                                    }
                                ]}
                                onItemClick={({ detail }) => {
                                    switch (detail.id) {
                                        case 'show':
                                            handleVisibilityChange(true);
                                            break;
                                        case 'hide':
                                            handleVisibilityChange(false);
                                            break;
                                    }
                                }}
                            >Actions</ButtonDropdown>
                            <Button
                                variant="normal"
                                disabled={singleItemActionsDisabled}
                                onClick={() => navigate(`/admin/demo/${selectedDemos[0].path}`)}
                            >Edit Demo</Button>
                            <Button variant="primary" onClick={() => navigate('/admin/demo')}>Add Demo</Button>
                        </SpaceBetween>

                    }
                >
                    Configured Demos
                </Header>
            }
            preferences={
                <CollectionPreferences
                    title="Preferences"
                    confirmLabel="Confirm"
                    cancelLabel="Cancel"
                    pageSizePreference={{
                        title: "Select page size",
                        options: [
                            { value: 5, label: "5 resources" },
                            { value: 10, label: "10 resources" },
                            { value: 25, label: "25 resources" },
                            { value: 50, label: "50 resources" }
                        ]
                    }}
                    preferences={{ pageSize: preferences.pageSize }}
                    onConfirm={({ detail }) => {
                        setPreferences({ pageSize: detail.pageSize ?? 10 });
                        setPageIndex(1);
                    }}
                />
            }
            pagination={
                <Pagination
                    currentPageIndex={pageIndex}
                    pagesCount={Math.ceil(filteredDemos.length / preferences.pageSize)}
                    onChange={({ detail }) => setPageIndex(detail.currentPageIndex)}
                />
            }
        />
    )
}
